/**
 * Key shape for a persisted view arrangement, scoped to the customer type it
 * was arranged under.
 *
 * What a grid shows is not the same grid for every customer: which columns and
 * stat cards exist, and which are worth seeing, follow the customer's
 * CustomerType. A user who works across customers of different types would
 * otherwise carry one type's arrangement into another's grid, so every saved
 * arrangement is scoped to the customer type as well as to the user — the
 * durable user preferences and the session-lifetime grid state alike, which is
 * why both dialects resolve their customer-type segment here.
 *
 * Views rendered above any customer scope — staff and global lists — share the
 * unscoped segment, so they still persist.
 *
 * Contains:
 * - UNSCOPED_CUSTOMER_TYPE_SEGMENT — the segment for a view outside customer scope
 * - scopedPreferenceKey — dotted user-preference key for any base key
 * - gridPreferenceKey — a grid's dotted user-preference key
 * - gridSessionKey — a grid's colon-delimited sessionStorage key
 *
 * Returns plain JS — no JSX, no redux.
 */

export const UNSCOPED_CUSTOMER_TYPE_SEGMENT = 'ct0';

/**
 * The one spelling of the customer-type half of a scoped key.
 *
 * @param {?(string|number)} customerTypeId - the scope customer's CustomerType
 *   id, or null outside customer scope.
 * @returns {string} `ct<customerTypeId>`, or the unscoped segment.
 */
function customerTypeSegment(customerTypeId) {
  return customerTypeId == null ? UNSCOPED_CUSTOMER_TYPE_SEGMENT : `ct${customerTypeId}`;
}

/**
 * The user-preference key a view persists one kind of arrangement under.
 *
 * @param {?string} baseKey - what the arrangement belongs to (e.g.
 *   'map.siteGroupList'). Null/blank means the view does not persist, and the
 *   caller passes the null key straight to useUserPreference.
 * @param {?(string|number)} customerTypeId - the scope customer's CustomerType
 *   id, or null outside customer scope.
 * @param {string} suffix - what is being saved, including its version where the
 *   shape is versioned (e.g. 'columns', 'columnVisibility.v2').
 * @returns {?string} `<baseKey>.ct<customerTypeId>.<suffix>`, or null when
 *   there is no base key.
 */
export function scopedPreferenceKey(baseKey, customerTypeId, suffix) {
  if (!baseKey) return null;
  return `${baseKey}.${customerTypeSegment(customerTypeId)}.${suffix}`;
}

/**
 * @param {?string} persistKey - the grid's own persistence scope (e.g.
 *   'siteGrid'), or null when the grid does not persist.
 * @param {?(string|number)} customerTypeId - the scope customer's CustomerType
 *   id, or null outside customer scope.
 * @param {string} suffix - what is being saved, including its version where the
 *   shape is versioned (e.g. 'columnWidths', 'columnVisibility.v2').
 * @returns {?string} `grid.<persistKey>.ct<customerTypeId>.<suffix>`, or null
 *   when there is no persistKey.
 */
export function gridPreferenceKey(persistKey, customerTypeId, suffix) {
  if (!persistKey) return null;
  return scopedPreferenceKey(`grid.${persistKey}`, customerTypeId, suffix);
}

/**
 * The sessionStorage key a grid keeps one piece of session-lifetime state
 * under — sort, search, pagination, filters, card filters, selection. Colon
 * delimited, because that is the spelling the session keys already had.
 *
 * @param {?string} persistKey - the grid's own persistence scope (e.g.
 *   'siteGrid'), or null when the grid does not persist.
 * @param {?(string|number)} customerTypeId - the scope customer's CustomerType
 *   id, or null outside customer scope.
 * @param {string} suffix - which piece of state (e.g. 'sort', 'cardFilters').
 * @returns {?string} `grid:<persistKey>:ct<customerTypeId>:<suffix>`, or null
 *   when there is no persistKey.
 */
export function gridSessionKey(persistKey, customerTypeId, suffix) {
  if (!persistKey) return null;
  return `grid:${persistKey}:${customerTypeSegment(customerTypeId)}:${suffix}`;
}
